<template>
    <div class="query-section">
        <h3 v-if="title">{{ title }}:</h3>
        <div v-if="textResponse" class="explanation-text">
            <div class="markdown-content custom-class">
                <Markdown :content="textResponse" />
            </div>
            <!-- {{ textResponse }} -->
        </div>

        <pre v-if="query" class="bg-light p-3 rounded">{{ query }}</pre>

        <div :class="['mt-3', isChatHistory ? '' : 'mb-4']" v-if="!isChatHistory || query">
            <button v-if="!hideButtons" class="btn btn-success me-2" @click="executeQuery" :disabled="localLoading || queryExecuted">
                Run Query
            </button>
            <button class="btn btn-secondary" @click="handleSaveQuery" :disabled="localLoading">
                Save Query
            </button>
        </div>

        <div v-if="isChatHistory">
            <div v-if="queryResultsProp">
                <h3>Query Results:</h3>
                <div v-if="queryResultsProp.error" class="alert alert-danger">
                    {{ queryResultsProp.error }}
                </div>
                <div v-else class="table-responsive">
                    <table class="table table-striped table-bordered">
                        <thead>
                            <tr>
                                <th v-for="(column, index) in queryResultsProp.column_info" :key="index">
                                    {{ column.name }}
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(row, rowIndex) in queryResultsProp.data" :key="rowIndex">
                                <td v-for="(column, colIndex) in queryResultsProp.column_info" :key="colIndex">
                                    {{ row[colIndex] }}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
            <div v-if="analysisResultProp" class="mt-4">
                <h3>Analysis:</h3>
                <div class="explanation-text">
                    <div class="markdown-content">
                        <Markdown :content="analysisResultProp" />
                    </div>
                    <!-- {{ analysisResultProp }} -->
                </div>
                <div v-if="adjustedQueryProp">
                    <QueryDisplay title="Adjusted Query" :query="adjustedQueryProp" :loading="loading" :isChatHistory="true"
                        :question="question" :database="database" :table="table" :resourceId="resourceId" :messagesList="messagesList"
                        :hideButtons="hideButtons" @loading-change="$emit('loading-change', $event)"
                        @save-query="$emit('save-query', $event)" @append-message="$emit('append-message', $event)" />
                </div>
            </div>
        </div>
        <div v-else>
            <div v-if="queryResults">
                <h3>Query Results:</h3>
                <div v-if="queryResults.error" class="alert alert-danger">
                    {{ queryResults.error }}
                </div>
                <div v-else class="table-responsive">
                    <table class="table table-striped table-bordered">
                        <thead>
                            <tr>
                                <th v-for="(column, index) in queryResults.column_info" :key="index">
                                    {{ column.name }}
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(row, rowIndex) in queryResults.data" :key="rowIndex">
                                <td v-for="(column, colIndex) in queryResults.column_info" :key="colIndex">
                                    {{ row[colIndex] }}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
            <div v-if="analysisResult" class="mt-4">
                <h3>Analysis:</h3>
                <div class="explanation-text">
                    <div class="markdown-content">
                        <Markdown :content="analysisResult" />
                    </div>
                    <!-- {{ analysisResult }} -->
                </div>

                <div v-if="adjustedQuery">
                    <QueryDisplay title="Adjusted Query" :query="adjustedQuery" :loading="loading" :question="question"
                        :database="database" :table="table" :resourceId="resourceId" :messagesList="messagesList" :hideButtons="hideButtons"
                        @loading-change="$emit('loading-change', $event)" @save-query="$emit('save-query', $event)" 
                        @append-message="$emit('append-message', $event)" @status-change="$emit('status-change', $event)"/>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import Markdown from "../components/Markdown.vue";
import untruncateJson from "untruncate-json";
import { mapActions } from 'vuex';
import { mergeAgentsQueryIntoUrl } from '@/utils/agentsApi';
export default {
    components: { Markdown },
    name: 'QueryDisplay',
    props: {
        title: {
            type: String,
            required: true
        },
        query: {
            type: String,
            required: true
        },
        textResponse: {
            type: String,
            default: ''
        },
        loading: {
            type: Boolean,
            default: false
        },
        question: {
            type: String,
            required: true
        },
        database: {
            type: String,
            required: true
        },
        table: {
            type: String,
            required: true
        },
        analysisResultProp: {
            type: String,
            default: ""
        },
        adjustedQueryProp: {
            type: String,
            default: ""
        },
        queryResultsProp: {
            type: Object,
            default: null
        },
        resourceId: {
            type: String,
            default: ""
        },
        messagesList: {
            type: Array,
            default: () => []
        },
        isChatHistory: {
            type: Boolean,
            default: false
        },
        hideButtons: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            queryExecuted: false,
            queryResults: null,
            analysisResult: null,
            localLoading: false
        }
    },
    methods: {
        ...mapActions({
            upsertChatMessage: "amdp/upsertChatMessage",
        }),
        untruncateJson,
        async executeQuery() {
            this.$emit('loading-change', true);
            this.$emit('status-change', null);
            this.localLoading = true;
            this.queryExecuted = true;
            console.log("Query: ", this.query)

            // create user message to chat (for execute query api)
            let user_message = {
                "role": "user",
                "content": "Execute the query and give results. In case of error, adjust the query.",
                "timestamp": new Date().toISOString(),
            }
            await this.upsertChatMessage({
                feature: "talk_to_my_data",
                resourceId: this.resourceId,
                message_payload: {
                    "messages": [user_message]
                },
            }).then(() => {
                this.$emit('append-message', user_message);
            }).catch((error) => {
                let err = error.response?.data.error ? error.response.data.error : error.message;
                console.error('Failed to create user message:', err);
                this.$store.dispatch('addToast', {
                    type: 'error',
                    message: `Error creating user message: ${err}`,
                });
            });

            try {
                const endpoint = mergeAgentsQueryIntoUrl(
                    `${this.$store.state.agentsUrl}/v1/talk_to_my_data/execute_query`,
                    this.$store.state
                );
                const response = await fetch(endpoint, {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${this.$store.state.accessToken}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        question: this.question,
                        generated_query: this.query,
                        db_name: this.database,
                        table_name: this.table,
                        access_token: this.$store.state.accessToken,
                        cid: this.$store.state.currentClient.cid,
                        messages: this.messagesList,
                    })
                });

                const reader = response.body.getReader();
                const decoder = new TextDecoder();
                let buffer = '';
                let loop = true
                let firstChunkReceived = false;
                while (loop) {
                    const { value, done } = await reader.read();
                    if (done) break;

                    buffer += decoder.decode(value, { stream: true });
                    const lines = buffer.split('\n');

                    // Process all complete lines
                    for (let i = 0; i < lines.length - 1; i++) {
                        const line = lines[i].trim();
                        if (line.startsWith('data: ')) {
                            try {
                                const data = JSON.parse(this.untruncateJson(line.slice(6)));
                                if (data.final_answer) {
                                    this.queryResults = data.query_result;
                                    this.analysisResult = data.final_answer;
                                    this.adjustedQuery = data.adjusted_query;
                                    if (!firstChunkReceived) {
                                        firstChunkReceived = true;
                                        this.$emit('status-change', null); // clear status when first chunk is received
                                    }
                                }
                            } catch (e) {
                                console.error('Error parsing JSON:', e);
                            }
                        } else if (line.startsWith('reason: ')) {
                            let reason = line.slice(8)
                            let toastoptions = {
                                message: `Blocked by guardrail: ${reason}`,
                                type: "error"
                            }
                            this.$store.dispatch('addToast', toastoptions);
                            
                            // add guardrail message to chat
                            let guardrail_message = {
                                "role": "assistant",
                                "content": {
                                    "guardrail_action": "guardrail_intervened",
                                    "guardrail_message": reason,
                                },
                                "timestamp": new Date().toISOString(),
                            }
                            await this.upsertChatMessage({
                                feature: "talk_to_my_data",
                                resourceId: this.resourceId,
                                message_payload: {
                                    "messages": [guardrail_message]
                                },
                            });
                            return;
                        } else if (line.startsWith('status: ') && !firstChunkReceived) {
                            const status = line.slice(8);
                            this.$emit('status-change', status);
                        }
                    }

                    // Keep the last incomplete line in the buffer
                    buffer = lines[lines.length - 1];
                }

                // create agent message to chat
                let agent_message = {
                    "role": "assistant",
                    "content": {
                        "query_result": this.queryResults,
                        "adjusted_query": this.adjustedQuery,
                        "final_answer": this.analysisResult,
                    },
                    "timestamp": new Date().toISOString(),
                }
                await this.upsertChatMessage({
                    feature: "talk_to_my_data",
                    resourceId: this.resourceId,
                    message_payload: {
                        "messages": [agent_message]
                    },
                }).then(() => {
                    this.$emit('append-message', agent_message);
                }).catch((error) => {
                    let err = error.response?.data.error ? error.response.data.error : error.message;
                    console.error('Failed to create agent message:', err);
                    this.$store.dispatch('addToast', {
                        type: 'error',
                        message: `Error creating agent message: ${err}`,
                    });
                });

            } catch (error) {
                console.error('Error executing query:', error);
                this.queryExecuted = false;
            } finally {
                this.localLoading = false;
                this.$emit('loading-change', false);
                this.$emit('status-change', null);
            }
        },
        handleSaveQuery() {
            this.$emit('save-query', this.query);
        }
    },
    emits: ['loading-change', 'save-query', 'append-message', 'status-change'],
}
</script>

<style scoped>

.table-responsive {
    max-height: 400px;
    overflow-y: auto;
}

table {
    margin-bottom: 0;
    background-color: white;
}

thead th {
    position: sticky;
    top: 0;
    background-color: #fff;
    z-index: 1;
}

.custom-class {
    width: 100%;
    overflow-x: scroll;
}
</style>

<style>
body.dark-mode .generated-query-display table {
    background-color: #1a1d23 !important;
    color: #e1e3e8;
}
body.dark-mode .generated-query-display thead th {
    background-color: #111317 !important;
    color: #a0a4b8;
}
body.dark-mode .generated-query-display td {
    color: #e1e3e8;
    border-color: #2e3238;
}
</style>